import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Collapse from '@mui/material/Collapse';
import Slider from '@mui/material/Slider';
import LoadingButton from '@mui/lab/LoadingButton';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { TrainingRun } from '../../services/TrainingRunsService';
import { AppContext } from '../../contexts/AppContext';
import { convertDistance, convertElevation, round } from '../../utils/UnitsUtils';
import { getTotalSectionNutrition } from '../../utils/NutritionUtils';
import NutritionProductsSection from '../nutrition/NutritionProductsSection';
import NutritionSummaryTabs from '../nutrition/NutritionSummaryTabs';
import NoteBox from '../plans/NoteBox';

interface TrainingRunItemProps {
  trainingRun: TrainingRun;
  onUpdate?: (updated: TrainingRun) => void;
}

export const TrainingRunItem: React.FC<TrainingRunItemProps> = observer(({ trainingRun, onUpdate }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userService, nutritionProductsService, trainingRunsService } = React.useContext(AppContext);
  const [expanded, setExpanded] = React.useState(false);
  const [editedRun, setEditedRun] = React.useState<TrainingRun>(trainingRun);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (!userService.user?.id) {
      return;
    }
    nutritionProductsService.loadNutritionProducts(userService.user.id);
  }, [nutritionProductsService, userService.user?.id]);

  React.useEffect(() => {
    setEditedRun(trainingRun);
  }, [trainingRun]);

  const debouncedSave = React.useMemo(
    () =>
      debounce(async (runToSave: TrainingRun) => {
        try {
          await trainingRunsService.saveTrainingRun(runToSave);
          if (onUpdate) {
            onUpdate(runToSave);
          }
        } catch (error) {
          console.error('Error saving training run:', error);
        }
      }, 1000, { leading: false, trailing: true }),
    [trainingRunsService, onUpdate]
  );

  React.useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  React.useEffect(() => {
    // Only save if the editedRun is different from the original trainingRun
    if (editedRun.id === trainingRun.id &&
        (editedRun.performance !== trainingRun.performance ||
         editedRun.gutIssues !== trainingRun.gutIssues ||
         editedRun.temperature !== trainingRun.temperature ||
         JSON.stringify(editedRun.nutrition) !== JSON.stringify(trainingRun.nutrition))) {
      debouncedSave(editedRun);
    }
  }, [editedRun, debouncedSave, trainingRun]);

  const distanceUnits = userService.user?.distanceUnits || 'km';
  const elevationUnits = userService.user?.elevationUnits || 'm';

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const handleDelete = async () => {
    try {
      await trainingRunsService.deleteTrainingRun(trainingRun.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting training run:', error);
    }
  };

  const nutritionValues = getTotalSectionNutrition(
    editedRun.nutrition || [],
    nutritionProductsService.nutritionProducts
  );

  const timeInHours = editedRun.time / 60;
  const nutritionPerHour = {
    water: timeInHours ? nutritionValues.water / timeInHours : 0,
    calories: timeInHours ? nutritionValues.calories / timeInHours : 0,
    carbs: timeInHours ? nutritionValues.carbs / timeInHours : 0,
    protein: timeInHours ? nutritionValues.protein / timeInHours : 0,
    fat: timeInHours ? nutritionValues.fat / timeInHours : 0,
    sodium: timeInHours ? nutritionValues.sodium / timeInHours : 0,
    caffeine: timeInHours ? nutritionValues.caffeine / timeInHours : 0
  };

  return (
    <Box
      width="100%"
      marginBottom={2}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 2
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={isMobile ? 0.5 : 1}>
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1, mr: 1 }}>
          <Typography variant="h6" noWrap>{trainingRun.name}</Typography>
          <Tooltip title={intl.formatMessage({ id: 'action.edit' })}>
            <IconButton size="small" onClick={() => navigate(`/nutritional-training/edit/${trainingRun.id}`)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 2} sx={{ flexShrink: 0 }}>
          {!isMobile && <Typography variant="body2" fontWeight="700">💪{editedRun.performance}/10</Typography>}
          {!isMobile && <Typography variant="body2" fontWeight="700">🤢{editedRun.gutIssues}/10</Typography>}
          {!isMobile && <Typography variant="body2" fontWeight="700">🌡️{editedRun.temperature}/10</Typography>}
          <Tooltip title={intl.formatMessage({ id: 'action.delete' })}>
            <IconButton size="small" onClick={() => setDeleteDialogOpen(true)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {isMobile && (
        <Box display="flex" gap={1} marginBottom={1}>
          <Typography variant="body2" fontWeight="700">💪{editedRun.performance}/10</Typography>
          <Typography variant="body2" fontWeight="700">🤢{editedRun.gutIssues}/10</Typography>
          <Typography variant="body2" fontWeight="700">🌡️{editedRun.temperature}/10</Typography>
        </Box>
      )}

      {/* Summary Info */}
      <Box display="grid" sx={{ gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }} gap={1} marginBottom={1}>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'data.distanceTitle' })}
          </Typography>
          <Typography variant="body2">
            {convertDistance(trainingRun.distance, distanceUnits)} {distanceUnits}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'data.elevationGainTitle' })}
          </Typography>
          <Typography variant="body2">
            {convertElevation(trainingRun.elevationGain, elevationUnits)} {elevationUnits}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'data.elevationLossTitle' })}
          </Typography>
          <Typography variant="body2">
            {convertElevation(trainingRun.elevationLoss, elevationUnits)} {elevationUnits}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'data.dateTitle' })}
          </Typography>
          <Typography variant="body2">
            {dayjs(trainingRun.date).locale(intl.locale).format('ll')}
          </Typography>
        </Box>
      </Box>

      {/* Additional Summary Row */}
      <Box display="grid" sx={{ gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }} gap={1} marginBottom={1}>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'trainingRun.elapsedTime' })}
          </Typography>
          <Typography variant="body2">{formatTime(trainingRun.time)}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'trainingRun.carbsPerHour' })}
          </Typography>
          <Typography variant="body2">{round(nutritionPerHour.carbs, 0)}g</Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontSize={12} fontWeight="700">
            {intl.formatMessage({ id: 'trainingRun.sodiumPerHour' })}
          </Typography>
          <Typography variant="body2">{round(nutritionPerHour.sodium, 0)}mg</Typography>
        </Box>
      </Box>

      {/* Expand Button */}
      <Box display="flex" justifyContent="flex-end" marginTop={1}>
        <Button
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          size="small"
        >
          {intl.formatMessage({ id: 'trainingRun.nutritionAndSensations' })}
        </Button>
      </Box>

      {/* Expandable Content */}
      <Collapse in={expanded}>
        <Box marginTop={2}>
          {/* Row 1: Nutrition Products (left) and Nutrition Summary (right) */}
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} marginBottom={2}>
            {/* Left: Nutrition Products */}
            <Box flex={1}>
              <NutritionProductsSection
                nutrition={editedRun.nutrition || []}
                setNutrition={(nutrition) => setEditedRun({ ...editedRun, nutrition })}
                showAddFavorite={false}
              />
            </Box>

            {/* Right: Nutrition Summary Tabs */}
            <Box flex={1}>
              <NutritionSummaryTabs
                nutritionValues={{
                  hour: nutritionPerHour,
                  run: nutritionValues
                }}
                tabs={[
                  { value: "hour", labelId: "data.totalNutritionPerHour" },
                  { value: "run", labelId: "trainingRun.totalForRun" }
                ]}
                defaultTab="hour"
              />
            </Box>
          </Box>

          {/* Row 2: Sliders (full width) */}
          <Box marginBottom={2}>
            <Typography variant="body2" fontWeight="bold" marginBottom={1}>
              {intl.formatMessage({ id: 'trainingRun.feelings' })}
            </Typography>
            <Box
              display="grid"
              sx={{ gridTemplateColumns: { xs: 'auto 1fr', md: 'auto auto 1fr auto' } }}
              gap={2}
              alignItems="center"
            >
              <Typography variant="body2" noWrap>
                {intl.formatMessage({ id: 'trainingRun.performance' })} 💪
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
                {intl.formatMessage({ id: 'trainingRun.feltWeak' })}
              </Typography>
              <Slider
                value={editedRun.performance}
                onChange={(_, value) => setEditedRun({ ...editedRun, performance: value as number })}
                min={0}
                max={10}
                step={1}
                marks
              />
              <Typography variant="caption" color="text.secondary" align="right" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
                {intl.formatMessage({ id: 'trainingRun.feltStrong' })}
              </Typography>

              <Typography variant="body2" noWrap>
                {intl.formatMessage({ id: 'trainingRun.gutIssues' })} 🤢
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
                {intl.formatMessage({ id: 'trainingRun.noIssues' })}
              </Typography>
              <Slider
                value={editedRun.gutIssues}
                onChange={(_, value) => setEditedRun({ ...editedRun, gutIssues: value as number })}
                min={0}
                max={10}
                step={1}
                marks
              />
              <Typography variant="caption" color="text.secondary" align="right" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
                {intl.formatMessage({ id: 'trainingRun.strongIssues' })}
              </Typography>

              <Typography variant="body2" noWrap>
                {intl.formatMessage({ id: 'trainingRun.temperature' })} 🌡️
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
                {intl.formatMessage({ id: 'trainingRun.veryCold' })}
              </Typography>
              <Slider
                value={editedRun.temperature}
                onChange={(_, value) => setEditedRun({ ...editedRun, temperature: value as number })}
                min={0}
                max={10}
                step={1}
                marks
              />
              <Typography variant="caption" color="text.secondary" align="right" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
                {intl.formatMessage({ id: 'trainingRun.veryWarm' })}
              </Typography>
            </Box>
          </Box>

          {/* Row 3: Notes (full width) */}
          <Box>
            <Typography variant="body2" fontWeight="bold" marginBottom={1}>
              {intl.formatMessage({ id: 'plans.notes' })}
            </Typography>
            <NoteBox
              currentSection={0}
              note={editedRun.notes || ''}
              onChangeNote={(note) => setEditedRun({ ...editedRun, notes: note })}
            />
          </Box>
        </Box>
      </Collapse>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title">
          {intl.formatMessage({ id: 'trainingRun.deleteConfirmTitle' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {intl.formatMessage({ id: 'trainingRun.deleteConfirmMessage' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {intl.formatMessage({ id: 'action.cancel' })}
          </Button>
          <Button onClick={handleDelete} autoFocus>
            {intl.formatMessage({ id: 'action.delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default TrainingRunItem;
